import { Camera, type Intersection, PerspectiveCamera, OrthographicCamera } from 'three'
import * as THREE from 'three'
import { useMapStore } from '@/stores/mapStore.ts'
import { nextTick } from 'vue'
import { useShipActionStore } from '@/stores/shipActionStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { useMapShipStore } from '@/stores/mapShipStore.ts'
import { useMapCoordinateStore } from '@/stores/mapCoordinateStore.ts'
import { useMapPlanetOwnerStore } from '@/stores/mapPlanetOwnerStore.ts'
import { zoomFactor } from '@/enums/zoomFactor.ts'
import { MapShipActionPaths } from '@/models/Map/MapShipActionPaths.ts'
import { useTableStore } from '@/stores/tableStore.ts'
import { MapCrossHairUpdate } from '@/models/Map/MapCrosshair.ts'
import { useMapSettingsStore } from '@/stores/mapSettingsStore.ts'
import * as Sentry from '@sentry/vue'
import { logger } from '@/models/logger';

export class MapMouseController {
  isDragging: boolean
  startMousePosition = { x: 0, y: 0 }
  dragMousePosition = { x: 0, y: 0 }
  container: HTMLElement
  camera: Camera
  scene: THREE.Scene
  raycaster: THREE.Raycaster
  mouse: THREE.Vector2
  mapStore: any
  gameStore: any
  tableStore: any
  shipActionPaths: any
  hoverXpos: number
  hoverYpos: number
  shipActionStore: any
  mapPlanetOwnerStore: any
  mapCoordinateStore: any
  mapShipStore: any
  renderer: THREE.WebGLRenderer
  isTouchDragging: boolean
  lastTouchPosition: { x: number; y: number }
  // Zoom constants
  minZoomFactor: number = 0.1 // Max zoom out (corresponds to zoomFactor.Max)
  maxZoomFactor: number = 10 // Max zoom in (corresponds to zoomFactor.Planet)
  zoomSpeed: number = 0.001 // How fast zooming occurs
  // Middle mouse button state
  isMiddleMouseDown: boolean = false

  constructor(
    container: HTMLElement,
    camera: THREE.Camera,
    scene: THREE.Scene,
    renderer: THREE.WebGLRenderer
  ) {
    this.container = container
    this.camera = camera
    this.scene = scene
    this.renderer = renderer
    this.isDragging = false
    this.isTouchDragging = false
    this.lastTouchPosition = { x: 0, y: 0 }

    // Apply touch-action: none to prevent default browser touch behaviors
    this.container.style.touchAction = 'none'

    this.raycaster = new THREE.Raycaster()
    this.mouse = new THREE.Vector2()

    this.mapStore = useMapStore()
    this.gameStore = useGameStore()
    this.shipActionStore = useShipActionStore()
    this.mapPlanetOwnerStore = useMapPlanetOwnerStore()
    this.mapCoordinateStore = useMapCoordinateStore()
    this.mapShipStore = useMapShipStore()
    this.tableStore = useTableStore()

    // Ship Action paths, for when hovering over ships (to show their paths)
    this.shipActionPaths = new MapShipActionPaths(this.scene)
    this.hoverXpos = 0
    this.hoverYpos = 0

    // Define the area selector box
    const boxVertices = new Float32Array([
      0,
      0,
      0.09, // Top-left corner
      0,
      0,
      0.09, // Top-right corner
      0,
      0,
      0.09, // Bottom-right corner
      0,
      0,
      0.09, // Bottom-left corner
      0,
      0,
      0.09 // Back to Top-left to close the loop
    ])
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(boxVertices, 3))
    const material = new THREE.LineBasicMaterial({ color: 0x0000ff })
    this.mapStore.areaBox = new THREE.Line(geometry, material)
    this.mapStore.areaBox.visible = false
    this.mapStore.areaBox.frustumCulled = false
    this.scene.add(this.mapStore.areaBox)

    // Bind the methods to the instance
    this.onMouseDown = this.onMouseDown.bind(this)
    this.onMouseLeave = this.onMouseLeave.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)
    this.onClick = this.onClick.bind(this)
    this.onWheel = this.onWheel.bind(this)
    this.onTouchStart = this.onTouchStart.bind(this)
    this.onTouchMove = this.onTouchMove.bind(this)
    this.onTouchEnd = this.onTouchEnd.bind(this)
    this.onAuxClick = this.onAuxClick.bind(this)

    this.container.addEventListener('mousedown', this.onMouseDown)
    this.container.addEventListener('mouseleave', this.onMouseLeave)
    this.container.addEventListener('mousemove', this.onMouseMove)
    this.container.addEventListener('mouseup', this.onMouseUp)
    this.container.addEventListener('click', this.onClick)
    this.container.addEventListener('wheel', this.onWheel)
    this.container.addEventListener('touchstart', this.onTouchStart, { passive: false })
    this.container.addEventListener('touchmove', this.onTouchMove, { passive: false })
    this.container.addEventListener('touchend', this.onTouchEnd)
    this.container.addEventListener('auxclick', this.onAuxClick) // For middle mouse button clicks
  }

  destroy() {
    // Remove event listeners when destroying the instance
    this.container.removeEventListener('mousedown', this.onMouseDown)
    this.container.removeEventListener('mouseleave', this.onMouseLeave)
    this.container.removeEventListener('mousemove', this.onMouseMove)
    this.container.removeEventListener('mouseup', this.onMouseUp)
    this.container.removeEventListener('click', this.onClick)
    this.container.removeEventListener('wheel', this.onWheel)
    this.container.removeEventListener('auxclick', this.onAuxClick)
  }

  // Control rendering quality based on zooming state
  setRenderQuality(highQuality: boolean) {
    // If the renderer supports quality settings, apply them here
    if (highQuality) {
      // Restore high quality rendering after zoom
      // Clamp pixel ratio between 1.0 and 1.5 (instead of using full devicePixelRatio)
      const clampedRatio = Math.min(Math.max(window.devicePixelRatio, 1.0), 1.5)
      if (this.renderer.getPixelRatio() < clampedRatio) {
        this.renderer.setPixelRatio(clampedRatio)
      }
    } else {
      // Reduce quality during zoom for better performance
      // Always set to 1.0 during zooming
      if (this.renderer.getPixelRatio() > 1.0) {
        this.renderer.setPixelRatio(1.0)
      }
    }
  }

  onMouseDown(event: MouseEvent) {
    this.mapStore.playerNameTooltipVisible = false

    // Track middle mouse button state
    if (event.button === 1) {
      // Middle mouse button
      this.isMiddleMouseDown = true
      event.preventDefault()
    }

    this.startDrag(event)
    this.mapClick(event) // moved to mouse down because of the area selection
  }

  onMouseLeave(event: MouseEvent) {
    this.stopDrag(event)
  }

  onMouseMove(event: MouseEvent) {
    if (this.isDragging) {
      this.dragMap(event)
    }
    this.updateMousePosition(event)
    this.mapMove(event)
    this.areaSelectMove(event)
    this.hoverEvent(event)
  }

  onMouseUp(event: MouseEvent) {
    // Reset middle mouse button state
    if (event.button === 1) {
      this.isMiddleMouseDown = false
    }

    this.stopDrag(event)
    this.areaSelectEnd(event)
    //this.mapStore.loadVisibleSectors();
  }

  onClick(event: MouseEvent) {
    this.updateMousePosition(event)
  }
  /*************************** Hover *************************/

  hoverEvent(_event: MouseEvent) {
    this.raycaster.setFromCamera(this.mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.scene.children)

    let found = false
    let planetIntersectionData = null

    // First pass - collect data and check for ships specifically
    for (const intersect of intersects) {
      if (intersect.object.name === 'blackHole') {
        //this.setCursor('pointer');
      }

      const data = this.getCoordinateData(intersect)
      if (!data) continue // Skip if data is null or undefined

      // Check for ships first
      if (data.hasPlayerShips) {
        // Prevent creating a new path if the mouse is still on the same coordinate
        if (this.hoverXpos !== data.xPos || this.hoverYpos !== data.yPos) {
          this.shipActionPaths.create(data.xPos, data.yPos)
          this.hoverXpos = data.xPos
          this.hoverYpos = data.yPos
        }

        // Show tooltip for player ship
        this.mapStore.playerNameTooltipVisible = true
        this.mapStore.playerNameTooltipOwnerId = this.gameStore.player?.id || 0
        this.mapStore.playerNameTooltipShipCount = data.ships[this.gameStore.player?.id] || 0
        this.mapStore.playerNameTooltipName = ''
        this.mapStore.playerNameTooltipCompatibility = ''

        found = true
        break // Exit early if we found a ship
      }

      // Store planet data for later if no ships are found
      if (data.isPlanet) {
        planetIntersectionData = data
      }

      if (data.isWormhole) {
        found = true
      }
    }

    // If we didn't find a ship with direct hover, check if there are ships near the planet
    if (!found && planetIntersectionData) {
      const planetX = planetIntersectionData.xPos
      const planetY = planetIntersectionData.yPos

      // Process planet data for tooltips first (prioritize planet over nearby ships)
      if (planetIntersectionData.planetOwnerId !== 0) {
        // Show tooltip with name
        this.mapStore.playerNameTooltipVisible = true
        this.mapStore.playerNameTooltipOwnerId = planetIntersectionData.planetOwnerId
        this.mapStore.playerNameTooltipShipCount =
          planetIntersectionData.ships[planetIntersectionData.planetOwnerId] || 0
        this.mapStore.playerNameTooltipName = ''
        this.mapStore.playerNameTooltipCompatibility = ''
        found = true
      }

      // Check for unowned planets without ships
      if (!found && planetIntersectionData && planetIntersectionData.planetOwnerId === 0 && Object.keys(planetIntersectionData.ships).length === 0) {
        this.mapStore.playerNameTooltipVisible = true
        this.mapStore.playerNameTooltipOwnerId = 0
        this.mapStore.playerNameTooltipShipCount = 0
        this.mapStore.playerNameTooltipName = ''
        this.mapStore.playerNameTooltipCompatibility = planetIntersectionData.compatibility
        found = true
      }

      // Only search for ships in orbit if the planet doesn't have an owner (so no planet tooltip to show)
      if (!found) {
        // Expanded search for ships around the planet (orbit range)
        for (let dx = -3; dx <= 3; dx++) {
          for (let dy = -3; dy <= 3; dy++) {
            const orbitX = planetX + dx
            const orbitY = planetY + dy

            if (this.mapCoordinateStore.hasByCoordinates(orbitX, orbitY)) {
              const orbitData = this.mapCoordinateStore.getDataForRayCasting(orbitX, orbitY)
              if (!orbitData) continue // Skip if data is null or undefined

              if (orbitData.hasPlayerShips) {
                // Found a ship in orbit
                this.shipActionPaths.create(orbitX, orbitY)
                this.hoverXpos = orbitX
                this.hoverYpos = orbitY

                // Show tooltip for player ship
                this.mapStore.playerNameTooltipVisible = true
                this.mapStore.playerNameTooltipOwnerId = this.gameStore.player?.id || 0
                this.mapStore.playerNameTooltipShipCount =
                  orbitData.ships[this.gameStore.player?.id] || 0
                this.mapStore.playerNameTooltipName = ''
                this.mapStore.playerNameTooltipCompatibility = ''

                found = true
                break
              }
            }
          }
          if (found) break
        }
      }

      //Auto select planet if it is the player
      if (planetIntersectionData.planetOwnerId == this.gameStore.player?.id) {
        this.tableStore.planets.singleSelectedId = planetIntersectionData.id
      }
    }

    // Process other intersections if we still haven't found anything
    if (!found) {
      for (const intersect of intersects) {
        const data = this.getCoordinateData(intersect)
        if (!data) continue // Skip if data is null or undefined

        if (Object.keys(data.ships).length > 0) {
          this.mapStore.playerNameTooltipVisible = true
          this.mapStore.playerNameTooltipOwnerId = Object.keys(data.ships).pop()
          this.mapStore.playerNameTooltipShipCount =
            data.ships[this.mapStore.playerNameTooltipOwnerId]
          this.mapStore.playerNameTooltipName = ''
          this.mapStore.playerNameTooltipCompatibility = ''
          found = true
          break
        }
      }
    }

    // Check for stars with no players or ships
    if (!found) {
      for (const intersect of intersects) {
        const data = this.getCoordinateData(intersect)
        if (!data) continue // Skip if data is null or undefined

        if (data.isStar && Object.keys(data.ships).length === 0) {
          this.mapStore.playerNameTooltipVisible = true
          this.mapStore.playerNameTooltipOwnerId = 0
          this.mapStore.playerNameTooltipShipCount = 0
          this.mapStore.playerNameTooltipName = data.typeName
          this.mapStore.playerNameTooltipCompatibility = ''
          found = true
          break
        }
      }
    }

    // Clean up if nothing was found
    if (this.shipActionPaths !== null && !found) {
      this.shipActionPaths.deleteLines()
      this.hoverXpos = 0
      this.hoverYpos = 0
    }

    if (!found) {
      this.mapStore.playerNameTooltipVisible = false
    }
  }

  /*************************** Move *************************/

  mapMove(event: MouseEvent) {
    this.raycaster.setFromCamera(this.mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.scene.children)

    if (this.shipActionStore.pendingAction !== '') {
      this.mapPendingAction(event, intersects)
    } else {
      this.mapMoveToolTip(intersects)
    }

    this.getLocation(event, intersects)
  }

  getLocation(event: MouseEvent, intersects: Array<Intersection>) {
    for (const intersect of intersects) {
      if ((intersect as any).object.name === 'backPlane') {
        this.mapStore.mouseXPos = Math.ceil((intersect as any).point.x)
        this.mapStore.mouseYPos = Math.ceil((intersect as any).point.y)
        break
      }
    }
  }

  /**
   * Handle pending action when moving mouse
   *
   * @param event
   * @param intersects
   */
  mapPendingAction(event: MouseEvent, intersects: Array<Intersection>) {
    for (const intersect of intersects) {
      //Detect if this is the backPlane
      if (intersect.object.name === 'backPlane') {
        const data = this.getCoordinateData(intersect)
        if (this.shipActionStore.pendingAction === 'move') {
          if (data.xPos == 0) {
            if (event.shiftKey) {
              // Free Movement Enabled
              this.setCursor('move')
              this.mapStore.shipActionPath.removePendingAction()
              this.mapStore.shipActionPath.setPendingAction(
                (intersect as any).point.x,
                (intersect as any).point.y
              )
              this.renderMap()
              break
            } else {
              // Clear Selection for grid movement
              this.setCursor('default')
              this.mapStore.shipActionPath.removePendingAction()
              this.renderMap()
              break
            }
          } else {
            // Grid Movement Enabled
            this.setCursor('move')
            this.mapStore.shipActionPath.setPendingAction(data.xPos, data.yPos)
            this.renderMap()
            break
          }
        } else if (this.shipActionStore.pendingAction === 'follow') {
          if (data.xPos == 0) {
            // Clear Selection for grid movement
            this.setCursor('default')
            this.mapStore.shipActionPath.removePendingAction()
            this.renderMap()
            break
          } else if (data.canBeFollowed) {
            // Grid Movement Enabled
            this.setCursor('move')
            this.mapStore.shipActionPath.setPendingAction(data.xPos, data.yPos)
            this.renderMap()
            break
          }
        } else if (this.shipActionStore.pendingAction === 'followAttack') {
          if (data.xPos == 0) {
            // Clear Selection for grid movement
            this.setCursor('default')
            this.mapStore.shipActionPath.removePendingAction()
            this.renderMap()
            break
          } else if (data.canBeFollowed) {
            // Grid Movement Enabled
            this.setCursor('move')
            this.mapStore.shipActionPath.setPendingAction(data.xPos, data.yPos)
            this.renderMap()
            break
          }
        } else if (this.shipActionStore.pendingAction === 'explore') {
          break
        } else if (this.shipActionStore.pendingAction === 'repair') {
          break
        } else if (this.shipActionStore.pendingAction === 'attack') {
          if (data.xPos == 0) {
            this.mapStore.shipActionPath.removePendingAction()
            this.renderMap()
          } else {
            // Attack the selected position
            this.mapStore.shipActionPath.setPendingAction(data.xPos, data.yPos)
            this.renderMap()
            this.setCursor('crosshair')
            break
          }
        } else if (this.shipActionStore.pendingAction === 'settle') {
          if (data.xPos == 0) {
            this.mapStore.shipActionPath.removePendingAction()
            this.renderMap()
          } else if (data.canBeSettled) {
            // Settle the selected position
            this.mapStore.shipActionPath.setPendingAction(data.xPos, data.yPos)
            this.renderMap()
            this.setCursor('crosshair')
            break
          }
        } else {
          this.setCursor('default')
        }
      }
    }
  }

  /**
   * Display cursor on planes that can do tooltips
   * @param intersects
   */
  mapMoveToolTip(intersects: Array<Intersection>) {
    for (const intersect of intersects) {
      const data = this.getCoordinateData(intersect)
      if (data.hasTooltip) {
        this.setCursor('pointer')
        break
      } else {
        this.setCursor('default')
      }
    }
  }

  /**
   * Figure out what we are hovering on
   * @param intersect
   */
  getCoordinateData(intersect: Intersection) {
    if ((intersect as any).object.name === 'backPlane') {
      const x = Math.ceil((intersect as any).point.x)
      const y = Math.ceil((intersect as any).point.y)

      // Increase the detection radius for all zoom levels
      // to ensure we can detect ships and show their action paths
      let radius = 2 // Default minimum radius increased from 1

      if (this.mapStore.zoomFactor === zoomFactor.Planet) {
        radius = 2 // Increased from 1
      } else if (this.mapStore.zoomFactor === zoomFactor.Solar) {
        radius = 2 // Increased from 1
      } else if (this.mapStore.zoomFactor === zoomFactor.Local) {
        radius = 4 // Increased from 3
      } else if (this.mapStore.zoomFactor === zoomFactor.Sector) {
        radius = 6 // Increased from 4
      } else if (this.mapStore.zoomFactor === zoomFactor.Quadrant) {
        radius = 8 // Increased from 5
      } else if (this.mapStore.zoomFactor === zoomFactor.Max) {
        radius = 10 // Increased from 6
      } else {
        // For intermediate zoom levels, calculate radius based on nearest preset zoom levels
        if (
          this.mapStore.zoomFactor > zoomFactor.Solar &&
          this.mapStore.zoomFactor < zoomFactor.Planet
        ) {
          // Between Solar and Planet
          radius = 2
        } else if (
          this.mapStore.zoomFactor > zoomFactor.Local &&
          this.mapStore.zoomFactor < zoomFactor.Solar
        ) {
          // Between Local and Solar
          radius = 3
        } else if (
          this.mapStore.zoomFactor > zoomFactor.Sector &&
          this.mapStore.zoomFactor < zoomFactor.Local
        ) {
          // Between Sector and Local
          radius = 5
        } else if (
          this.mapStore.zoomFactor > zoomFactor.Quadrant &&
          this.mapStore.zoomFactor < zoomFactor.Sector
        ) {
          // Between Quadrant and Sector
          radius = 7
        } else if (
          this.mapStore.zoomFactor > zoomFactor.Max &&
          this.mapStore.zoomFactor < zoomFactor.Quadrant
        ) {
          // Between Max and Quadrant
          radius = 9
        }
      }

      const list = this.getNearbyCoordinates(x, y, radius)
      for (const coords of list) {
        if (this.mapCoordinateStore.hasByCoordinates(coords.x, coords.y)) {
          return this.mapCoordinateStore.getDataForRayCasting(coords.x, coords.y) // Exits the loop
        }
      }
      return {
        id: 0,
        isPlanet: false,
        isWormhole: false,
        planetOwnerId: 0,
        canBeSettled: false,
        hasTooltip: false,
        hasPlayerShips: false,
        ships: {},
        xPos: 0,
        yPos: 0
      }
    } else {
      return {
        id: 0,
        isPlanet: false,
        isWormhole: false,
        planetOwnerId: 0,
        canBeSettled: false,
        hasTooltip: false,
        hasPlayerShips: false,
        ships: {},
        xPos: 0,
        yPos: 0
      }
    }
  }

  getNearbyCoordinates(x: number, y: number, radius: number) {
    const nearbyCoords = []

    // Iterate through all possible grid coordinates within the radius
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dy = -radius; dy <= radius; dy++) {
        // Add nearby coordinates to the list
        nearbyCoords.push({ x: x + dx, y: y + dy })
      }
    }

    return nearbyCoords
  }
  /*************************** click *************************/

  /**
   * Process click on map
   *
   * @param event
   */
  async mapClick(event: MouseEvent) {
    if (event.button === 0) {
      if (this.mapStore.toolTipVisible) {
        this.mapStore.toolTipVisible = false
        await nextTick()
      }

      if (this.hasDragged(event)) {
        this.stopDrag(event)
      } else {
        if (this.shipActionStore.pendingAction !== '') {
          this.pendingActionClick(event)
        } else {
          if (!this.openToolTip(event)) {
            //no tooltip, so can start the area selection
            this.areaSelectStart(event)
          }
        }
      }
    }
  }

  areaSelectStart(event: MouseEvent) {
    if (event.button !== 0) return
    if (this.shipActionStore.initialized) return

    this.mapStore.areaStartTime = Date.now()
    this.mapStore.areaStart = { x: this.mapStore.mouseXPos, y: this.mapStore.mouseYPos } // Can use mapStore if applicable
    this.mapStore.areaSelecting = true
    this.mapStore.areaActionSelecting = false

    // Set initial size of the box
    this.updateAreaBox(
      this.mapStore.areaStart.x,
      this.mapStore.areaStart.y,
      this.mapStore.areaStart.x,
      this.mapStore.areaStart.y
    )

    logger.debug('Area Select Start')
  }

  areaSelectMove(_event: MouseEvent) {
    if (!this.mapStore.areaSelecting) return
    if (this.shipActionStore.initialized) return

    this.mapStore.areaBox.visible = true

    this.mapStore.areaEnd = { x: this.mapStore.mouseXPos, y: this.mapStore.mouseYPos }

    // Dynamically resize the box
    this.updateAreaBox(
      this.mapStore.areaStart.x,
      this.mapStore.areaStart.y,
      this.mapStore.areaEnd.x,
      this.mapStore.areaEnd.y
    )

    logger.debug('Area Select Move')
  }

  areaSelectEnd(event: MouseEvent) {
    if (event.button !== 0) return
    if (!this.mapStore.areaSelecting) return
    if (this.shipActionStore.initialized) return

    const elapsedTime = Date.now() - this.mapStore.areaStartTime

    if (elapsedTime > 200) {
      logger.debug('Area Select End')
      const rect = this.container.getBoundingClientRect()
      this.mapStore.areaMouseEnd = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      }
      this.mapStore.areaSelecting = false
      this.mapStore.areaActionSelecting = true

      // Determine the top-left and bottom-right coordinates
      this.mapStore.areaBoxTL = {
        x: Math.min(this.mapStore.areaStart.x, this.mapStore.areaEnd.x),
        y: Math.max(this.mapStore.areaStart.y, this.mapStore.areaEnd.y)
      }

      this.mapStore.areaBoxRB = {
        x: Math.max(this.mapStore.areaStart.x, this.mapStore.areaEnd.x),
        y: Math.min(this.mapStore.areaStart.y, this.mapStore.areaEnd.y)
      }

      logger.debug('Area Select Time: ' + elapsedTime)
      logger.debug('Area Select Start: ' + JSON.stringify(this.mapStore.areaStart))
      logger.debug('Area Select End: ' + JSON.stringify(this.mapStore.areaEnd))
    } else {
      logger.debug('Area Select End (Too Fast)')
      this.mapStore.disableAreaSelection()
    }
  }

  updateAreaBox(x1: number, y1: number, x2: number, y2: number) {
    const boxVertices = new Float32Array([
      x1,
      y1,
      1.09, // Top-left corner
      x2,
      y1,
      1.09, // Top-right corner
      x2,
      y2,
      1.09, // Bottom-right corner
      x1,
      y2,
      1.09, // Bottom-left corner
      x1,
      y1,
      1.09 // Back to Top-left to close the loop
    ])

    const geometry = this.mapStore.areaBox.geometry as THREE.BufferGeometry
    geometry.setAttribute('position', new THREE.BufferAttribute(boxVertices, 3))
    geometry.attributes.position.needsUpdate = true // Ensure the update happens
  }
  pendingActionClick(_event: MouseEvent) {
    this.raycaster.setFromCamera(this.mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.scene.children)
    for (const intersect of intersects) {
      if (intersect.object.name === 'backPlane') {
        const data = this.getCoordinateData(intersect)

        if (this.shipActionStore.pendingAction === 'move') {
          // Process Movement
          let x = 0
          let y = 0
          if (data.xPos == 0) {
            // Free Movement
            x = Math.ceil((intersect as any).point.x)
            y = Math.ceil((intersect as any).point.y)
          } else {
            // Grid Movement
            x = data.xPos
            y = data.yPos
          }
          this.mapStore.shipActionPath.queuePendingAction(x, y, 0, 0)
          this.renderMap()
          break
        } else if (this.shipActionStore.pendingAction === 'follow') {
          if (data.xPos != 0) {
            // Add Sentry breadcrumb to track modal opening
            Sentry.addBreadcrumb({
              category: 'ship-action',
              message: 'Opening follow modal',
              level: 'info',
              data: {
                action: 'follow',
                xPos: data.xPos,
                yPos: data.yPos,
                hasCoordinate: this.mapCoordinateStore.hasByCoordinates(data.xPos, data.yPos),
                fleetId: this.shipActionStore.fleetId,
                shipsCount: this.shipActionStore.ships.length
              }
            })
            this.gameStore.modalType = 'mapFollow'
            this.gameStore.modalData = {
              xPos: data.xPos,
              yPos: data.yPos
            }
            break
          }
        } else if (this.shipActionStore.pendingAction === 'followAttack') {
          if (data.xPos != 0) {
            // Add Sentry breadcrumb to track modal opening
            Sentry.addBreadcrumb({
              category: 'ship-action',
              message: 'Opening followAttack modal',
              level: 'info',
              data: {
                action: 'followAttack',
                xPos: data.xPos,
                yPos: data.yPos,
                hasCoordinate: this.mapCoordinateStore.hasByCoordinates(data.xPos, data.yPos),
                fleetId: this.shipActionStore.fleetId,
                shipsCount: this.shipActionStore.ships.length
              }
            })
            this.gameStore.modalType = 'mapFollowAttack'
            this.gameStore.modalData = {
              xPos: data.xPos,
              yPos: data.yPos
            }
            break
          }
        } else if (this.shipActionStore.pendingAction === 'attack') {
          if (data.xPos != 0) {
            // Add Sentry breadcrumb to track modal opening
            Sentry.addBreadcrumb({
              category: 'ship-action',
              message: 'Opening attack modal',
              level: 'info',
              data: {
                action: 'attack',
                xPos: data.xPos,
                yPos: data.yPos,
                hasCoordinate: this.mapCoordinateStore.hasByCoordinates(data.xPos, data.yPos),
                fleetId: this.shipActionStore.fleetId,
                shipsCount: this.shipActionStore.ships.length
              }
            })
            this.gameStore.modalType = 'mapAttack'
            this.gameStore.modalData = {
              xPos: data.xPos,
              yPos: data.yPos
            }
            break
          }
        } else if (this.shipActionStore.pendingAction === 'settle' && data.canBeSettled) {
          if (data.xPos != 0) {
            this.shipActionStore.queuePendingAction(data.xPos, data.yPos, 0, 0)
            this.renderMap()
            break
          }
        }
      }
    }
  }

  /**
   * Open tooltip on map
   *
   * @param event
   */
  openToolTip(_event: MouseEvent): boolean {
    this.raycaster.setFromCamera(this.mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.scene.children)
    for (const intersect of intersects) {
      const data = this.getCoordinateData(intersect)
      if (data.hasTooltip) {
        this.mapStore.tooltipX = data.xPos
        this.mapStore.tooltipY = data.yPos
        this.mapStore.toolTipVisible = true
        return true
      }
      if (intersect.object.name === 'blackHole') {
        // Black Hole TODO: Add tooltip
        return true
      }
    }
    return false
  }

  /*************************** Dragging *************************/

  /**
   * Start Dragging
   *
   * @param event
   */
  startDrag(event: MouseEvent) {
    // Right click should start dragging
    if (event.button === 2) {
      this.startMousePosition = { x: event.clientX, y: event.clientY }
      this.dragMousePosition = { x: event.clientX, y: event.clientY }
      this.isDragging = true
    }
  }

  /**
   * Stop Dragging
   *
   * @param event
   */
  stopDrag(_event: MouseEvent) {
    // Right click should start dragging
    if (this.isDragging) {
      this.isDragging = false
      this.setCursor('default')
    }
  }

  /**
   * After click, did we drag?
   *
   *
   * @param event
   * @param threshold
   */
  hasDragged(event: MouseEvent, threshold: number = 5): boolean {
    // Right click should start dragging
    if (event.button === 2) {
      const deltaX = Math.abs(this.startMousePosition.x - event.clientX)
      const deltaY = Math.abs(this.startMousePosition.y - event.clientY)

      return deltaX > threshold || deltaY > threshold
    }
    return false
  }

  /**
   * Process drag
   *
   * @param event
   */
  async dragMap(event: MouseEvent) {
    // Right click should start dragging
    this.setCursor('grabbing')
    const deltaX = event.clientX - this.dragMousePosition.x
    const deltaY = event.clientY - this.dragMousePosition.y
    this.camera.position.x -= deltaX * (0.5 / this.mapStore.zoomFactor)
    this.camera.position.y += deltaY * (0.5 / this.mapStore.zoomFactor)
    this.mapStore.cameraXPos = this.camera.position.x
    this.mapStore.cameraYPos = this.camera.position.y
    this.dragMousePosition = { x: event.clientX, y: event.clientY }
    this.renderMap()

    // Hide area selection and menu when dragging/scrolling the map
    if (this.mapStore.areaSelecting || this.mapStore.areaActionSelecting) {
      this.mapStore.disableAreaSelection()
    }

    if (this.mapStore.crossHairVisible) {
      this.mapStore.crossHairVisible = false
      new MapCrossHairUpdate(this.scene, this.mapStore.zoomFactor)
    }
  }

  /*************************** Tools *************************/

  /**
   * Update the mouse position on map
   *
   * @param event
   */
  updateMousePosition(event: MouseEvent) {
    const rect = this.container.getBoundingClientRect()
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
  }

  /**
   * Render map
   */
  renderMap() {
    this.renderer.render(this.scene, this.camera)
  }

  /**
   * Set cursor on map
   * @param type
   */
  setCursor(type: string) {
    this.container.style.cursor = type
  }

  onWheel(event: WheelEvent) {
    // Check if mouse wheel zoom is enabled
    const mapSettingsStore = useMapSettingsStore()

    if (mapSettingsStore.enableMouseWheelZoom) {
      // ZOOMING BEHAVIOR
      // Reduce quality during zoom for better performance
      this.setRenderQuality(false)

      // Calculate the new zoom factor
      let newZoomFactor = this.mapStore.zoomFactor - event.deltaY * this.zoomSpeed

      // Clamp the zoom factor between min and max values
      newZoomFactor = Math.max(this.minZoomFactor, Math.min(this.maxZoomFactor, newZoomFactor))

      // Apply the new zoom factor
      this.mapStore.zoomFactor = newZoomFactor

      // Update objects with the new zoom factor
      this.mapCoordinateStore.applyZoomFactorAll(newZoomFactor)

      // Update ship action paths ETA
      if (this.shipActionPaths) {
        this.shipActionPaths.updateZoom(newZoomFactor)
      }

      // Update the camera zoom
      if (this.camera instanceof PerspectiveCamera || this.camera instanceof OrthographicCamera) {
        this.camera.zoom = newZoomFactor
        this.camera.updateProjectionMatrix()
      }

      // Render the changes
      this.renderMap()

      // Restore quality after a brief delay
      setTimeout(() => {
        this.setRenderQuality(true)
        this.renderMap()
      }, 100)
    } else {
      // PANNING BEHAVIOR - Use wheel for panning when zoom is disabled
      event.preventDefault()

      // Determine pan amount (adjust these values as needed for desired sensitivity)
      const panSpeed = 10 / this.mapStore.zoomFactor

      // Handle both vertical and horizontal scroll
      // Most mouse wheels scroll vertically, but some can scroll horizontally
      // Shift+wheel is also often used for horizontal scrolling

      // Vertical scroll (or primary scroll direction)
      if (event.deltaY !== 0) {
        this.camera.position.y += event.deltaY * 0.01 * panSpeed
        this.mapStore.cameraYPos = this.camera.position.y
      }

      // Horizontal scroll (often shift+wheel or trackpad horizontal gesture)
      if (event.deltaX !== 0) {
        this.camera.position.x -= event.deltaX * 0.01 * panSpeed
        this.mapStore.cameraXPos = this.camera.position.x
      }

      // Render the changes
      this.renderMap()

      // Update crosshair if needed
      if (this.mapStore.crossHairVisible) {
        this.mapStore.crossHairVisible = false
        new MapCrossHairUpdate(this.scene, this.mapStore.zoomFactor)
      }
    }
  }

  onTouchStart(event: TouchEvent) {
    if (event.touches.length !== 1) {
      return
    }

    this.isTouchDragging = true
    this.lastTouchPosition = { x: event.touches[0].clientX, y: event.touches[0].clientY }
  }

  onTouchMove(event: TouchEvent) {
    if (event.touches.length !== 1) {
      return
    }

    if (this.isTouchDragging) {
      event.preventDefault() // Prevent default scrolling behavior

      // Calculate the movement delta
      const touch = event.touches[0]
      const deltaX = (touch.clientX - this.lastTouchPosition.x) * (0.5 / this.mapStore.zoomFactor)
      const deltaY = (touch.clientY - this.lastTouchPosition.y) * (0.5 / this.mapStore.zoomFactor)

      // Update the camera position
      this.camera.position.x -= deltaX
      this.camera.position.y += deltaY
      this.mapStore.cameraXPos = this.camera.position.x
      this.mapStore.cameraYPos = this.camera.position.y

      // Store the new position for the next move event
      this.lastTouchPosition = { x: touch.clientX, y: touch.clientY }

      // Update the rendering to reflect the new camera position
      this.renderMap()
    }
  }

  onTouchEnd(event: TouchEvent) {
    if (event.touches.length === 0) {
      this.isTouchDragging = false
    }
  }

  // Handle middle mouse button clicks
  onAuxClick(event: MouseEvent) {
    if (event.button === 1) {
      // Middle mouse button
      event.preventDefault()

      // Toggle zoom between min and max zoom levels
      let newZoomValue
      if (this.mapStore.zoomFactor > (this.minZoomFactor + this.maxZoomFactor) / 2) {
        newZoomValue = this.minZoomFactor
      } else {
        newZoomValue = this.maxZoomFactor
      }

      // Apply the new zoom factor
      this.mapStore.zoomFactor = newZoomValue

      // Update objects with the new zoom factor
      this.mapCoordinateStore.applyZoomFactorAll(newZoomValue)

      // Update ship action paths ETA
      if (this.shipActionPaths) {
        this.shipActionPaths.updateZoom(newZoomValue)
      }

      // Update the camera zoom
      if (this.camera instanceof PerspectiveCamera || this.camera instanceof OrthographicCamera) {
        this.camera.zoom = newZoomValue
        this.camera.updateProjectionMatrix()
      }

      // Render the changes
      this.renderMap()
    }
  }
}
